import { Box, Container, Link, Paper, Typography } from "@mui/material";

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="h5"
    sx={{
      color: "#0F5BA7",
      fontWeight: 700,
      mt: 5,
      mb: 2,
    }}
  >
    {children}
  </Typography>
);

const PrivacyPolicy = () => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
        py: { xs: 3, md: 6 },
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            p: { xs: 3, md: 6 },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: "#0F5BA7",
              fontWeight: 700,
              borderBottom: "3px solid #0F5BA7",
              pb: 1,
              mb: 3,
            }}
          >
            Privacy Policy
          </Typography>

          <Typography mb={3}>
            <strong>Effective Date:</strong> July 16, 2026
          </Typography>

          <Typography paragraph>
            SkuVahn ("we", "our", or "us") respects your privacy and is
            committed to protecting your personal information. This Privacy
            Policy explains how we collect, use, store, and protect your
            information when you use the <strong>SkuVahn</strong> mobile
            application.
          </Typography>

          <Box
            sx={{
              bgcolor: "#eef6ff",
              borderLeft: "5px solid #0F5BA7",
              p: 2,
              borderRadius: 1,
              my: 3,
            }}
          >
            <Typography>
              By using the SkuVahn application, you agree to the collection and
              use of information in accordance with this Privacy Policy.
            </Typography>
          </Box>

          <SectionTitle>1. About SkuVahn</SectionTitle>

          <Typography paragraph>
            SkuVahn is a school transportation management platform designed to
            help parents monitor and manage school transportation for their
            children while enabling registered drivers and administrators to
            efficiently manage routes, vehicles, bookings, and trips.
          </Typography>

          <SectionTitle>2. Information We Collect</SectionTitle>

          <Typography fontWeight={600}>Personal Information</Typography>

          <Box component="ul" sx={{ pl: 3 }}>
            <li>Full Name</li>
            <li>Mobile Number</li>
            <li>Email Address (if provided)</li>
            <li>Profile Information</li>
          </Box>

          <Typography fontWeight={600} mt={3}>
            Driver Information
          </Typography>

          <Typography paragraph mt={1}>
            To verify drivers, we may collect:
          </Typography>

          <Box component="ul" sx={{ pl: 3 }}>
            <li>Aadhaar details</li>
            <li>Vehicle Registration Certificate (RC)</li>
            <li>Police Verification documents</li>
            <li>Driver profile details</li>
          </Box>

          <Typography fontWeight={600} mt={3}>
            Location Information
          </Typography>

          <Typography paragraph mt={1}>
            The application collects precise location information from
            registered drivers during active trips in order to:
          </Typography>

          <Box component="ul" sx={{ pl: 3 }}>
            <li>Provide live vehicle tracking</li>
            <li>Calculate Estimated Time of Arrival (ETA)</li>
            <li>Monitor routes</li>
            <li>Improve passenger safety</li>
            <li>Enable administrators to monitor ongoing trips</li>
          </Box>

          <Typography paragraph>
            Parents can only view the live location of vehicles associated with
            their own bookings.
          </Typography>

          <Typography fontWeight={600}>Video Streaming</Typography>

          <Typography paragraph mt={1}>
            Where supported, SkuVahn may display live video streams from cameras
            installed inside registered school vehicles for monitoring and
            safety purposes.
          </Typography>

          <Typography paragraph>
            SkuVahn does not record or store video unless explicitly stated
            within the application or required by the organization operating the
            transport service.
          </Typography>

          <Typography fontWeight={600}>Device Information</Typography>

          <Typography paragraph mt={1}>
            We may automatically collect:
          </Typography>

          <Box component="ul" sx={{ pl: 3 }}>
            <li>Device model</li>
            <li>Operating System version</li>
            <li>Application version</li>
            <li>Device identifiers required for security</li>
            <li>Error logs</li>
          </Box>

          <SectionTitle>3. How We Use Your Information</SectionTitle>

          <Box component="ul" sx={{ pl: 3 }}>
            <li>Create and manage user accounts</li>
            <li>Verify registered drivers</li>
            <li>Provide transportation booking services</li>
            <li>Display live vehicle tracking</li>
            <li>Improve safety features</li>
            <li>Respond to customer support requests</li>
            <li>Maintain platform security</li>
            <li>Detect fraud or misuse</li>
            <li>Comply with legal obligations</li>
          </Box>

          <SectionTitle>4. Permissions Used</SectionTitle>

          <Box component="ul" sx={{ pl: 3 }}>
            <li>Location</li>
            <li>Camera (where applicable)</li>
            <li>Photos/Files (for document uploads)</li>
            <li>Notifications</li>
            <li>Internet Access</li>
          </Box>

          <Typography paragraph>
            Permissions are requested only when required for the relevant
            functionality.
          </Typography>

          <SectionTitle>5. Data Sharing</SectionTitle>

          <Typography paragraph>
            SkuVahn does not sell your personal information.
          </Typography>

          <Box component="ul" sx={{ pl: 3 }}>
            <li>With authorized school administrators</li>
            <li>
              With assigned drivers and parents as required for transportation
              services
            </li>
            <li>When required by applicable law</li>
            <li>To protect the safety and security of users</li>
          </Box>

          <SectionTitle>6. Data Security</SectionTitle>

          <Typography paragraph>
            We use industry-standard security practices including encrypted
            network communication (HTTPS), secure authentication, and access
            controls to protect your information.
          </Typography>

          <Typography paragraph>
            Although we strive to use commercially acceptable means to protect
            your data, no method of transmission over the Internet or electronic
            storage is completely secure.
          </Typography>

          <SectionTitle>7. Data Retention</SectionTitle>

          <Typography paragraph>
            Personal information is retained only for as long as necessary to
            provide our services, comply with legal obligations, resolve
            disputes, and enforce our agreements.
          </Typography>

          <Typography paragraph>
            Driver verification documents may be retained where required for
            operational or regulatory purposes.
          </Typography>

          <SectionTitle>8. Children's Privacy</SectionTitle>

          <Typography paragraph>
            SkuVahn is intended for use by parents, registered drivers, and
            authorized administrators.
          </Typography>

          <Typography paragraph>
            Children cannot create accounts or independently use the
            application. We do not knowingly collect personal information
            directly from children.
          </Typography>

          <SectionTitle>9. Third-Party Services</SectionTitle>

          <Typography paragraph>
            The current version of the application does not use Firebase
            services or integrate any payment gateway.
          </Typography>

          <Typography paragraph>
            If additional third-party services are introduced in future
            releases, this Privacy Policy will be updated accordingly.
          </Typography>

          <SectionTitle>10. Your Rights</SectionTitle>

          <Box component="ul" sx={{ pl: 3 }}>
            <li>Access your personal information</li>
            <li>Correct inaccurate information</li>
            <li>Delete your account (subject to legal obligations)</li>
            <li>Raise concerns regarding your privacy</li>
          </Box>

          <SectionTitle>11. Changes to this Privacy Policy</SectionTitle>

          <Typography paragraph>
            We may update this Privacy Policy periodically. Changes become
            effective immediately after being posted on this page.
          </Typography>

          <SectionTitle>12. Contact Us</SectionTitle>

          <Typography paragraph>
            <strong>SkuVahn</strong>
          </Typography>

          <Typography>
            Website:{" "}
            <Link
              href="https://skuvahn.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://skuvahn.com
            </Link>
          </Typography>

          <Typography mt={1}>
            Email:{" "}
            <Link href="mailto:skuvahn@gmail.com">
              skuvahn@gmail.com
            </Link>
          </Typography>

          <Box
            sx={{
              mt: 6,
              pt: 3,
              borderTop: "1px solid #ddd",
            }}
          >
            <Typography color="text.secondary">
              © 2026 SkuVahn. All Rights Reserved.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;